import { useCallback } from 'react';
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import logger from '../utils/logger';

const db = getFirestore();

const resolveCommentId = (comment) =>
  comment?.id ||
  comment?.commentId ||
  comment?.commentID ||
  comment?.comment_id ||
  comment?.legacyId ||
  comment?.firebaseId ||
  null;

const resolveReplyId = (reply) =>
  reply?.id ||
  reply?.replyId ||
  reply?.replyID ||
  reply?.reply_id ||
  reply?.legacyId ||
  reply?.firebaseId ||
  null;

const parseIndex = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const idx = Number(value);
  return Number.isInteger(idx) && idx >= 0 ? idx : null;
};

const findCommentIndex = (comments, commentId, commentIndex = null) => {
  const list = Array.isArray(comments) ? comments : [];
  if (commentId !== null && commentId !== undefined && String(commentId) !== '') {
    const targetId = String(commentId);
    const byId = list.findIndex(
      (comment) =>
        resolveCommentId(comment) != null &&
        String(resolveCommentId(comment)) === targetId
    );
    if (byId !== -1) return byId;
  }
  const idx = parseIndex(commentIndex);
  return idx != null && idx < list.length ? idx : -1;
};

const useComments = (setSongs) => {
  const addComment = useCallback(async (songId, newComment) => {
    if (!songId || !newComment.userId || !newComment.displayName || !newComment.text) {
      logger.error('Error: One or more required fields are undefined.');
      throw new Error('Invalid comment data');
    }

    const songRef = doc(db, 'songs', songId);
    const commentId = String(
      newComment.commentId || newComment.id || Date.now()
    );
    const commentToAdd = {
      id: commentId,
      userId: newComment.userId,
      userName: newComment.displayName,
      commentText: newComment.text,
      mentionedHandles: newComment.mentionedHandles || [],
      timestamp: Timestamp.now(),
      likes: [],
      replies: [],
    };

    const songSnapshot = await getDoc(songRef);
    const songData = songSnapshot.data();
    if (!songData) {
      throw new Error('Song not found');
    }

    try {
      const updatedComments = [...(songData.comments || []), commentToAdd];
      await updateDoc(songRef, { comments: updatedComments });
      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId ? { ...song, comments: updatedComments } : song
        )
      );
    } catch (error) {
      logger.error('Error adding comment:', error);
      throw error;
    }

    return commentToAdd.id;
  }, [setSongs]);

  const addReply = useCallback(async (songId, commentId, newReply, commentIndex = null) => {
    const songRef = doc(db, 'songs', songId);
    try {
      const songSnapshot = await getDoc(songRef);
      const songData = songSnapshot.data();
      if (songData?.comments) {
        if (commentId == null && commentIndex == null) {
          logger.warn('Unable to add reply: missing comment identifier');
          return null;
        }

        const targetIndex = findCommentIndex(
          songData.comments,
          commentId,
          commentIndex
        );
        if (targetIndex === -1) {
          throw new Error('Unable to add reply: comment not found');
        }

        const replyId = String(
          newReply.replyId || newReply.commentId || newReply.id || Date.now()
        );
        let createdReplyId = null;
        const updatedComments = songData.comments.map((comment, index) => {
          if (index !== targetIndex) return comment;

          const reply = {
            id: replyId,
            userId: newReply.userId,
            userName: newReply.displayName,
            commentText: newReply.text,
            mentionedHandles: newReply.mentionedHandles || [],
            timestamp: Timestamp.now(),
            likes: [],
          };
          createdReplyId = reply.id;
          return { ...comment, replies: [...(comment.replies || []), reply] };
        });
        if (!createdReplyId) {
          throw new Error('Unable to add reply: comment not found');
        }
        await updateDoc(songRef, { comments: updatedComments });
        setSongs((prevSongs) =>
          prevSongs.map((song) =>
            song.id === songId ? { ...song, comments: updatedComments } : song
          )
        );
        return createdReplyId;
      }
    } catch (error) {
      logger.error('Error in addReply:', error);
      throw error;
    }
    return null;
  }, [setSongs]);

  const deleteComment = useCallback(async (songId, commentId, commentIndex = null) => {
    try {
      const songRef = doc(db, 'songs', songId);
      const songSnapshot = await getDoc(songRef);
      const songData = songSnapshot.data();

      if (songData?.comments) {
        const targetId =
          commentId !== null && commentId !== undefined ? String(commentId) : null;
        if (targetId === null && (commentIndex === null || commentIndex === undefined)) {
          logger.warn('deleteComment called without a valid identifier', { songId });
          return;
        }

        const updatedComments = songData.comments.filter((comment, index) => {
          const currentId = resolveCommentId(comment);
          if (targetId) return String(currentId ?? '') !== targetId;
          if (commentIndex !== null && commentIndex !== undefined) return index !== commentIndex;
          return true;
        });

        await updateDoc(songRef, { comments: updatedComments });
        setSongs((prevSongs) =>
          prevSongs.map((song) =>
            song.id === songId ? { ...song, comments: updatedComments } : song
          )
        );
        logger.info('Comment deleted successfully.', { songId, commentId: targetId, commentIndex });
      } else {
        logger.error('Song or comments not found');
      }
    } catch (error) {
      logger.error('Error deleting comment:', error);
    }
  }, [setSongs]);

  const deleteReply = useCallback(async (songId, commentId, replyIndex, commentIndex = null) => {
    try {
      const songRef = doc(db, 'songs', songId);
      const songSnapshot = await getDoc(songRef);
      const songData = songSnapshot.data();

      if (songData?.comments) {
        const targetId =
          commentId !== null && commentId !== undefined ? String(commentId) : null;
        if (targetId === null && (commentIndex === null || commentIndex === undefined)) {
          logger.warn('deleteReply called without a valid comment identifier', { songId, replyIndex });
          return;
        }

        const updatedComments = songData.comments.map((comment, index) => {
          const commentMatchesById =
            targetId !== null && resolveCommentId(comment) !== null
              ? String(resolveCommentId(comment)) === targetId
              : false;
          const commentMatchesByIndex =
            commentIndex !== null && commentIndex !== undefined
              ? index === commentIndex
              : false;

          if (commentMatchesById || commentMatchesByIndex) {
            return {
              ...comment,
              replies: (comment.replies || []).filter((_, idx) => idx !== replyIndex),
            };
          }
          return comment;
        });

        await updateDoc(songRef, { comments: updatedComments });
        setSongs((prevSongs) =>
          prevSongs.map((song) =>
            song.id === songId ? { ...song, comments: updatedComments } : song
          )
        );
        logger.info('Reply deleted successfully.', { songId, commentId: targetId, commentIndex, replyIndex });
      } else {
        logger.error('Song or comments not found');
      }
    } catch (error) {
      logger.error('Error deleting reply:', error);
    }
  }, [setSongs]);

  const likeComment = useCallback(async (songId, commentId, userId, commentIndex = null) => {
    if ((!commentId && commentIndex == null) || !userId) {
      const error = new Error('Unable to like comment: missing identifiers');
      logger.warn(error.message, { commentId, commentIndex, userId });
      throw error;
    }

    const songRef = doc(db, 'songs', songId);
    const songSnapshot = await getDoc(songRef);
    const songData = songSnapshot.data();

    if (!Array.isArray(songData?.comments)) {
      throw new Error('Song has no comments');
    }

    const targetIndex = findCommentIndex(
      songData.comments,
      commentId,
      commentIndex
    );
    if (targetIndex === -1) {
      throw new Error('Comment not found');
    }

    const targetComment = songData.comments[targetIndex];
    const resolvedCommentId = resolveCommentId(targetComment) || commentId;
    const alreadyLiked = (targetComment.likes || []).includes(userId);

    if (!alreadyLiked) {
      const updatedComments = songData.comments.map((comment, index) => {
        if (index !== targetIndex) return comment;
        return { ...comment, likes: [...new Set([...(comment.likes || []), userId])] };
      });

      await updateDoc(songRef, { comments: updatedComments });
      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId ? { ...song, comments: updatedComments } : song
        )
      );
    }

    return {
      resolvedCommentId,
      commentIndex: targetIndex,
    };
  }, [setSongs]);

  const unlikeComment = useCallback(async (songId, commentId, userId, commentIndex = null) => {
    try {
      if ((!commentId && commentIndex == null) || !userId) {
        logger.warn('Unable to unlike comment: missing identifiers', { commentId, commentIndex, userId });
        return;
      }

      const songRef = doc(db, 'songs', songId);
      const songSnapshot = await getDoc(songRef);
      const songData = songSnapshot.data();

      if (songData?.comments) {
        const targetIndex = findCommentIndex(
          songData.comments,
          commentId,
          commentIndex
        );
        if (targetIndex === -1) {
          throw new Error('Comment not found');
        }

        const targetComment = songData.comments[targetIndex];
        const resolvedCommentId = resolveCommentId(targetComment) || commentId;

        const updatedComments = songData.comments.map((comment, index) => {
          if (index !== targetIndex) return comment;
          return { ...comment, likes: (comment.likes || []).filter((id) => id !== userId) };
        });

        await updateDoc(songRef, { comments: updatedComments });
        setSongs((prevSongs) =>
          prevSongs.map((song) =>
            song.id === songId ? { ...song, comments: updatedComments } : song
          )
        );

        logger.info('Comment unliked successfully.', { commentId: resolvedCommentId, commentIndex: targetIndex });

        try {
          await httpsCallable(functions, 'deleteCommentLikeNotification')({
            songId,
            commentId: resolvedCommentId != null ? String(resolvedCommentId) : null,
            isReply: false,
          });
        } catch (notifError) {
          logger.error('Error deleting comment like notification:', notifError);
        }
      }
    } catch (error) {
      logger.error('Error unliking comment:', error);
      throw error;
    }
  }, [setSongs]);

  const findReplyIndex = (comment, replyId, replyIndex = null) => {
    const replies = comment?.replies || [];
    if (replyId !== null && replyId !== undefined && String(replyId) !== '') {
      const targetReplyId = String(replyId);
      const byId = replies.findIndex(
        (reply) =>
          resolveReplyId(reply) != null &&
          String(resolveReplyId(reply)) === targetReplyId
      );
      if (byId !== -1) return byId;
    }
    const idx = parseIndex(replyIndex);
    return idx != null && idx < replies.length ? idx : -1;
  };

  const likeReply = useCallback(async (
    songId,
    commentId,
    replyId,
    userId,
    commentIndex = null,
    replyIndex = null
  ) => {
    if ((!commentId && commentIndex == null) || !userId) {
      throw new Error('Unable to like reply: missing identifiers');
    }
    if (!replyId && replyIndex == null) {
      throw new Error('Unable to like reply: missing reply identifier');
    }

    const songRef = doc(db, 'songs', songId);
    const songSnapshot = await getDoc(songRef);
    const songData = songSnapshot.data();

    if (!Array.isArray(songData?.comments)) {
      throw new Error('Song has no comments');
    }

    const targetCommentIndex = findCommentIndex(
      songData.comments,
      commentId,
      commentIndex
    );
    if (targetCommentIndex === -1) throw new Error('Comment not found');

    const comment = songData.comments[targetCommentIndex];
    const resolvedCommentId = resolveCommentId(comment) || commentId;
    const targetReplyIndex = findReplyIndex(comment, replyId, replyIndex);
    if (targetReplyIndex === -1) throw new Error('Reply not found');

    const targetReply = comment.replies[targetReplyIndex];
    const resolvedReplyId = resolveReplyId(targetReply) || replyId;
    const alreadyLiked = (targetReply.likes || []).includes(userId);

    if (!alreadyLiked) {
      const updatedComments = songData.comments.map((c, index) => {
        if (index !== targetCommentIndex) return c;
        const updatedReplies = (c.replies || []).map((reply, rIndex) => {
          if (rIndex !== targetReplyIndex) return reply;
          return { ...reply, likes: [...new Set([...(reply.likes || []), userId])] };
        });
        return { ...c, replies: updatedReplies };
      });

      await updateDoc(songRef, { comments: updatedComments });
      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId ? { ...song, comments: updatedComments } : song
        )
      );
    }

    return {
      resolvedCommentId,
      commentIndex: targetCommentIndex,
      resolvedReplyId,
      replyIndex: targetReplyIndex,
    };
  }, [setSongs]);

  const unlikeReply = useCallback(async (
    songId,
    commentId,
    replyId,
    userId,
    commentIndex = null,
    replyIndex = null
  ) => {
    try {
      if ((!commentId && commentIndex == null) || !userId) {
        logger.warn('Unable to unlike reply: missing identifiers', { commentId, commentIndex, replyId, replyIndex, userId });
        return;
      }
      if (!replyId && replyIndex == null) {
        logger.warn('Unable to unlike reply: missing reply identifier', { commentId, replyId, replyIndex });
        return;
      }

      const songRef = doc(db, 'songs', songId);
      const songSnapshot = await getDoc(songRef);
      const songData = songSnapshot.data();

      if (songData?.comments) {
        const targetCommentIndex = findCommentIndex(
          songData.comments,
          commentId,
          commentIndex
        );
        if (targetCommentIndex === -1) throw new Error('Comment not found');

        const comment = songData.comments[targetCommentIndex];
        const resolvedCommentId = resolveCommentId(comment) || commentId;
        const targetReplyIndex = findReplyIndex(comment, replyId, replyIndex);
        if (targetReplyIndex === -1) throw new Error('Reply not found');

        const updatedComments = songData.comments.map((c, index) => {
          if (index !== targetCommentIndex) return c;
          const updatedReplies = (c.replies || []).map((reply, rIndex) => {
            if (rIndex !== targetReplyIndex) return reply;
            return { ...reply, likes: (reply.likes || []).filter((id) => id !== userId) };
          });
          return { ...c, replies: updatedReplies };
        });

        await updateDoc(songRef, { comments: updatedComments });
        setSongs((prevSongs) =>
          prevSongs.map((song) =>
            song.id === songId ? { ...song, comments: updatedComments } : song
          )
        );

        try {
          await httpsCallable(functions, 'deleteCommentLikeNotification')({
            songId,
            commentId: resolvedCommentId != null ? String(resolvedCommentId) : null,
            isReply: true,
          });
        } catch (notifError) {
          logger.error('Error deleting reply like notification:', notifError);
        }
      }
    } catch (error) {
      logger.error('Error unliking reply:', error);
      throw error;
    }
  }, [setSongs]);

  return {
    addComment,
    addReply,
    deleteComment,
    deleteReply,
    likeComment,
    unlikeComment,
    likeReply,
    unlikeReply,
  };
};

export default useComments;
